import { FC, useEffect, useRef } from 'react';
import { HomefieldIconSignCancel } from '@dsg-tech/homefield-react-icons';
import { SearchData, StoreIdentifierName } from 'types';
import { useAppSelector } from 'store/store';
import { breakpointSelector } from 'store/selectors';
import { setSearchInput } from 'store/slices/sayt';
import { KEY_CODES } from 'utility/constants';
import { checkStores } from 'utility/utility';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { SearchProductResult } from '../../Search/types';
import SearchResults from '../../Search/SearchResults/SearchResults';
import SuggestionKeywordsResults from '../../Search/SuggestionKeywordsResults/SuggestionKeywordsResults';
import SuggestionProductsResults from '../../Search/SuggestionProductsResults/SuggestionProductsResults';
import SuggestionCategoriesResults from '../../Search/SuggestionCategoriesResults/SuggestionCategoriesResults';
import SuggestionBrandsResults from '../../Search/SuggestionBrandsResults/SuggestionBrandsResults';
import getSearchUtils from '../SearchUtils';
import './SAYTSuggestions.scss';

type SAYTProps = {
  storeIdentifierName: StoreIdentifierName;
  thumbnailApi: string;
  searchData: SearchData;
  submitSearch: (term: string, type: string, url?: string) => string;
  searchTerm: string;
  updateSearchTermSAYT: (term: string) => void;
  onCloseModal?: () => void;
  mobileDrawer?: boolean;
};

type CloseModalProps = {
  onCloseModal: () => void;
};

type ViewAllResultsButtonProps = {
  evaluateViewAllUrl: () => string;
};

const CloseButton = ({ onCloseModal }: CloseModalProps) => {
  return (
    <div className="close-button-wrapper">
      <button
        className="close-button hmf-display-flex hmf-align-items-center hmf-flex-column hmf-p-xxs"
        type="button"
        onClick={() => onCloseModal()}
        onKeyDown={(event) => {
          if (event.keyCode === KEY_CODES.SPACE) {
            onCloseModal();
          }
        }}
        tabIndex={0}>
        <HomefieldIconSignCancel width="24px" height="24px" />
        <span className="hmf-body-bold-s">Close</span>
      </button>
    </div>
  );
};

const ViewAllResultsButton = ({ evaluateViewAllUrl }: ViewAllResultsButtonProps) => {
  return (
    <p className="dsg-search-view-all-results">
      <a
        className="dsg-search-view-all-results__link hmf-label-m hmf-pb-xxxs"
        href={checkStores(evaluateViewAllUrl())}
        tabIndex={0}>
        View All Results
      </a>
    </p>
  );
};

const SAYTSuggestions: FC<SAYTProps> = ({
  storeIdentifierName,
  thumbnailApi,
  searchData,
  submitSearch,
  searchTerm,
  updateSearchTermSAYT,
  onCloseModal = () => {},
  mobileDrawer,
}) => {
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const productsContainerRef = useRef(null);
  const {
    evaluateViewAllUrl,
    clickSuggestedTerm,
    searchFromSuggestion,
    getProductImage,
    getSeoURL,
  } = getSearchUtils(storeIdentifierName);

  const suggestedHeading = (term: string, results: any) => {
    return results?.[term]?.length > 0 ? <h1>{term}</h1> : null;
  };

  const returnedProductJsx = (product: SearchProductResult) => {
    const seoURL = getSeoURL(product);
    const thumbnail = getProductImage(product, thumbnailApi);

    return (
      <SearchResults
        key={product.seoUrl}
        maxListPrice={product.maxListPrice}
        maxOfferPrice={product.maxOfferPrice}
        minListPrice={product.minListPrice}
        minOfferPrice={product.minOfferPrice}
        mapPriceIndicator={product.mapPriceIndicator}
        priceIndicator={product.priceIndicator}
        name={product.name}
        partNumber={product.partNumber}
        seoUrl={seoURL}
        thumbnail={thumbnail}
      />
    );
  };

  const suggestedProductsJsx = (
    <div className="dsg-search-products-container hmf-pb-xs" ref={productsContainerRef}>
      <p className="dsg-search-top-products hmf-label-s hmf-pt-s hmf-pl-xs hmf-mb-xxs">
        Top Products
      </p>
      <div className="dsg-search-products">
        {Array.isArray(searchData?.products)
          ? searchData.products.map((product) => returnedProductJsx(product))
          : null}
      </div>
      <a
        className="dsg-search-view-all-results__link hmf-label-m hmf-mt-xxs hmf-mb-xxxs hmf-py-s hmf-px-xs hmf-display-block"
        href={checkStores(evaluateViewAllUrl())}>
        View All Results
      </a>
    </div>
  );

  useEffect(() => {
    if (mobileDrawer) {
      const handleContainerHeight = () => {
        const visualViewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const searchResultsContainer = document.querySelector(
          '.dsg-search-results-container',
        ) as HTMLElement;
        if (searchResultsContainer) {
          searchResultsContainer.style.maxHeight = `${visualViewportHeight - 64}px`;
          searchResultsContainer.style.overflow = 'auto';
        }
      };

      handleContainerHeight();
      window.visualViewport?.addEventListener('resize', handleContainerHeight);
      return () => {
        window.visualViewport?.removeEventListener('resize', handleContainerHeight);
      };
    }
    return undefined;
  }, [mobileDrawer]);

  return (
    <div data-testid="sayt-results-container" className="dsg-search-results-container">
      {viewPortBreakpoint !== 'desktop' && !mobileDrawer && (
        <CloseButton onCloseModal={onCloseModal} />
      )}
      <div className="dsg-search-result-products">
        <div className="dsg-search-suggested-container" title="Suggested Keywords">
          <div className="dsg-search-suggested-keywords">
            {viewPortBreakpoint !== 'desktop' && (
              <div className="top-suggestions hmf-body-bold-l hmf-display-flex hmf-align-items-center hmf-pl-s">
                <span>Top Suggestions</span>
              </div>
            )}
            <ErrorBoundaryWithLogger componentId="SAYTSuggestions-SuggestionKeywordsResults">
              <SuggestionKeywordsResults
                suggestionResults={searchData.suggestions}
                submitSearch={submitSearch}
                searchTerm={searchTerm}
                setSearchTerm={setSearchInput}
                searchFromSuggestion={searchFromSuggestion}
                updateSearchTermSAYT={updateSearchTermSAYT}
                clickSuggestedTerm={clickSuggestedTerm}
                productsContainerRef={productsContainerRef}
              />
            </ErrorBoundaryWithLogger>
          </div>
          {viewPortBreakpoint === 'mobile' && (
            <div className="dsg-search-suggested-products" title="Suggested Products">
              {suggestedHeading('products', searchData.suggestions)}
              <ErrorBoundaryWithLogger componentId="SAYTSuggestions-SuggestionProductsResults">
                <SuggestionProductsResults
                  productResults={searchData.products}
                  clickSuggestedTerm={clickSuggestedTerm}
                />
              </ErrorBoundaryWithLogger>
            </div>
          )}
          <div className="dsg-search-suggested-categories" title="Suggested Categories">
            {suggestedHeading('categories', searchData.suggestions)}
            <ErrorBoundaryWithLogger componentId="SAYTSuggestions-SuggestionCategoriesResults">
              <SuggestionCategoriesResults
                suggestionResults={searchData.suggestions}
                submitSearch={submitSearch}
                searchFromSuggestion={searchFromSuggestion}
                clickSuggestedTerm={clickSuggestedTerm}
              />
            </ErrorBoundaryWithLogger>
          </div>
          <div className="dsg-search-suggested-brands" title="Suggested Brands">
            {suggestedHeading('brands', searchData.suggestions)}
            <ErrorBoundaryWithLogger componentId="SAYTSuggestions-SuggestionBrandsResults">
              <SuggestionBrandsResults
                suggestionResults={searchData.suggestions}
                submitSearch={submitSearch}
                setSearchTerm={setSearchInput}
                searchFromSuggestion={searchFromSuggestion}
                clickSuggestedTerm={clickSuggestedTerm}
              />
            </ErrorBoundaryWithLogger>
          </div>
        </div>
        {viewPortBreakpoint !== 'mobile' && suggestedProductsJsx}
      </div>
      {viewPortBreakpoint === 'mobile' && (
        <ErrorBoundaryWithLogger componentId="SAYTSuggestions-ViewAllResultsButton">
          <ViewAllResultsButton evaluateViewAllUrl={evaluateViewAllUrl} />
        </ErrorBoundaryWithLogger>
      )}
    </div>
  );
};
export default SAYTSuggestions;
